import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, CheckboxGroup, Spinner } from '@nextui-org/react';
import type { PronunciationDictionary, PronunciationDictionaryConfig } from '@circuitry-ai/doc-data';
import { normalizePronunciationDictionaryIds, useGetPronunciationDictionariesQuery } from '@circuitry-ai/doc-data';
import { DictionaryPaginationBar } from '@circuitry-ai/doc-voice-agent';

export interface PronunciationDictionarySectionProps {
  selectedDictionaryIds: string[];
  onUpdate: (config: PronunciationDictionaryConfig) => void;
  isUpdating?: boolean;
}

const PronunciationDictionarySection: React.FC<PronunciationDictionarySectionProps> = ({
  selectedDictionaryIds,
  onUpdate,
  isUpdating = false,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const { data: listResponse, isLoading, isFetching } = useGetPronunciationDictionariesQuery({
    page,
    per_page: perPage,
  });
  const dictionaries = listResponse?.data ?? [];
  const totalCount = listResponse?.total_count ?? 0;
  const [draftSelected, setDraftSelected] = useState<string[]>(selectedDictionaryIds);

  useEffect(() => {
    setDraftSelected(normalizePronunciationDictionaryIds(selectedDictionaryIds));
  }, [selectedDictionaryIds]);

  const sortedDictionaries = useMemo(
    () =>
      [...dictionaries].sort((a, b) =>
        String(a.name ?? '').localeCompare(String(b.name ?? ''))
      ),
    [dictionaries]
  );

  const applySelection = (nextSelected: string[]) => {
    const ids = normalizePronunciationDictionaryIds(nextSelected);
    onUpdate({
      enabled: ids.length > 0,
      dictionary_id: ids.length > 0 ? ids : null,
    });
  };

  const handleSelectedChange = (values: string[]) => {
    const nextSelected = values as string[];
    setDraftSelected(nextSelected);
    applySelection(nextSelected);
  };

  const handlePerPageChange = useCallback((nextPerPage: number) => {
    setPerPage(nextPerPage);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-foreground">{t('Pronunciation dictionaries')}</h3>
        <p className="text-sm text-default-500 mt-0.5">
          {t('Select dictionaries to guide how words are spoken by this voice agent.')}
        </p>
      </div>

      {isLoading || isFetching ? (
        <div className="flex items-center justify-center py-8 min-h-[120px]">
          <Spinner size="sm" label={t('Loading dictionaries...')} />
        </div>
      ) : sortedDictionaries.length === 0 ? (
        <p className="text-sm text-default-500">
          {t('No dictionaries available. Create one in Settings → Pronunciations.')}
        </p>
      ) : (
        <CheckboxGroup
          value={draftSelected}
          onValueChange={handleSelectedChange}
          isDisabled={isUpdating}
          classNames={{ wrapper: 'grid grid-cols-1 md:grid-cols-2 gap-2' }}
        >
          {sortedDictionaries.map((dictionary: PronunciationDictionary) => {
            const name = String(dictionary.name ?? '').trim();
            const dictionaryId = String(dictionary.id ?? '').trim();
            if (!dictionaryId) return null;
            const entryCount = dictionary.items_count ?? dictionary.items?.length ?? 0;
            return (
              <Checkbox
                key={dictionaryId}
                value={dictionaryId}
                classNames={{
                  base: 'w-full max-w-full m-0 rounded-lg border border-divider px-4 py-3 data-[selected=true]:border-primary data-[selected=true]:bg-primary/5',
                  label: 'w-full',
                }}
              >
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{name || dictionaryId}</span>
                  <span className="text-xs text-default-500">
                    {t('{{count}} entries', { count: entryCount })}
                  </span>
                </div>
              </Checkbox>
            );
          })}
        </CheckboxGroup>
      )}

      {!isLoading && totalCount > 0 && (
        <div className='mx-auto w-1/2'>
        <DictionaryPaginationBar
          page={page}
          perPage={perPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onPerPageChange={handlePerPageChange}
          isLoading={isFetching}
        />
        </div>
      )}
    </div>
  );
};

export default PronunciationDictionarySection;
